import { loginWithToken } from '@shared/utils/auth';
import { ErrorBoundary } from 'react-error-boundary';
import { ApolloError, useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';
import { css as emotionCSS } from '@emotion/react';
import { css } from '@mildang/styled-system/css';
import { Theme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Link from 'next/link';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Eye from '@mildang/icons/react/eye';
import EyeOff from '@mildang/icons/react/eye-off';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useErrorSnakbar from 'hooks/useErrorSnakbar';
import { mildangFirebaseAnalytics } from '@ui/utils/SALogEvent/mildangFirebase';
import TextFieldHook from '@mildang/design-system-legacy/esm/components/molecules/TextField/TextFieldHook';
import WelcomeBackgroundImage from 'components/WelcomeBackgroundImage';
import FindEmailDialog from './FindEmailDialog';
import ResetPasswordDialog from './ResetPasswordDialog';
import { toast } from '@ui/components/molecules/Toaster';
import { apolloLoginErrorParser } from '@shared/utils/errorParser';
import { useLingui } from '@lingui/react';
import { addError } from '../../../../mildang-ui/src/utils/apm/apm';
import { sanitizeReturnUrl } from '../../sanitize-return-url';
import { isDomainAuthProject } from '@shared/constants/projectTarget';

const schema = yup.object().shape({
  email: yup.string().email('올바르지 않은 이메일 입니다.').required('이메일은 필수값 입니다.'),
  password: yup.string().required('패스워드를 작성해주세요.'),
});

const rootStyle = css({
  position: 'relative',
  height: '100%',
});
const paperStyle = (theme: Theme) => emotionCSS`
  position: relative;
  height: 100%;
  padding: 40px;
  z-index: 1;
  border-radius: 16px;
  ${theme.breakpoints.down('sm')} {
    border-end-start-radius: 0px;
    border-end-end-radius: 0px;
    box-shadow: none;
    display: grid;
    grid-template-columns: 1fr;
    padding: 24px;
  }
`;
const formStyle = (theme: Theme) => emotionCSS`
  ${theme.breakpoints.down('sm')} {
    margin-left: auto;
    margin-right: auto;
    max-width: 336px;
    width: 100%;
  }
`;

const fieldsetStyle = css({
  display: 'grid',
  gridGap: '20px',
  justifyContent: 'space-between',
  alignContent: 'center',
  gridTemplateColumns: '1fr',
});
const imgWrapperStyle = (theme: Theme) => emotionCSS`
  ${theme.breakpoints.down('sm')} {
    display: none;
  }
`;

const otherActionsStyle = css({
  marginTop: '12px',
  display: 'grid',
  gridTemplateColumns: 'auto 1px auto 1px auto',
  gridColumnGap: '4px',
});

const dividerStyle = css({
  height: '12px',
  alignSelf: 'center',
});
const customButtonStyle = (theme: Theme) => emotionCSS`
  color: ${theme.palette.text.tertiary};
`;

interface IValues {
  email: string;
  password: string;
}

export const useLoginForm = () => {
  const { i18n } = useLingui();
  const client = useApolloClient();
  const { enqueueApolloError } = useErrorSnakbar();

  const form = useForm<IValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      const authUrl = isDomainAuthProject() ? '/login-with-domain' : process.env.NEXT_PUBLIC_GRAPHQL_URL;

      const authResult = await fetch(authUrl || '', {
        body: `{"query":"mutation ($loginWithDomainNameInput: LoginWithDomainNameInput!) {\\n  loginWithDomainName(loginWithDomainNameInput: $loginWithDomainNameInput) {\\n    needSignUp\\n    token\\n    user {\\n      id\\n      __typename\\n    }\\n    __typename\\n  }\\n}\\n","variables":{"loginWithDomainNameInput":{"email":"${data.email}","password":"${data.password}", "subdomain":"ihateflyingbugs"}}}`,
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
        },
      }).then((response) => response.json());

      if (authResult?.errors) {
        const message = apolloLoginErrorParser(i18n, {
          graphQLErrors: authResult.errors,
        } as ApolloError);

        addError(new Error(`LoginError:${authResult.errors[0].message}`), {
          'error.type': 'LoginError',
          'error.message': authResult.errors[0].message,
          'error.login.email': data.email,
          'apollo.client.error.extensions': authResult.errors[0].extensions,
        });

        return toast.error(message);
      }

      if (authResult?.data?.loginWithDomainName?.token) {
        loginWithToken(authResult?.data?.loginWithDomainName?.token);
      }

      mildangFirebaseAnalytics.logEvent('login_btn', {
        email: data.email,
      });
      client.cache.reset();
      const params = new URLSearchParams(window.location.search);
      const returnUrl = sanitizeReturnUrl(params.get('returnUrl'));
      window.location.href = returnUrl;
    } catch (e) {
      enqueueApolloError(e as any);
    }
  });

  return { ...form, onSubmit };
};

const LoginForm = ({ isNewWebsite = false }: { isNewWebsite: boolean }) => {
  const [showPassword, setShowPassword] = useState(false);

  const [openFindEmail, setOpenFindEmail] = useState(false);
  const [openResetPassword, setOpenResetPassword] = useState(false);

  const handleClickFindEmailOpen = () => {
    setOpenFindEmail(true);
  };
  const handleCloseFindEmail = () => {
    setOpenFindEmail(false);
  };
  const openResetPasswordOpen = () => {
    setOpenResetPassword(true);
  };
  const hideResetPassword = () => {
    setOpenResetPassword(false);
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const inputElement = useCallback((node) => {
    if (node) {
      node.focus();
    }
  }, []);

  const {
    formState: { isSubmitting },
    control,
    onSubmit,
  } = useLoginForm();

  return (
    <div className={rootStyle}>
      <ErrorBoundary FallbackComponent={() => null}>
        <WelcomeBackgroundImage css={imgWrapperStyle} />
      </ErrorBoundary>
      <Paper css={paperStyle} elevation={6}>
        <form css={formStyle} onSubmit={onSubmit} noValidate>
          <div className={fieldsetStyle}>
            <Typography
              // @ts-ignore
              css={(theme) => ({
                [theme.breakpoints.down('sm')]: {
                  ...theme.typography.h6,
                },
              })}
              variant="h5"
            >
              바로 시작해볼까요?
            </Typography>
            <TextFieldHook
              control={control}
              inputRef={inputElement}
              autoFocus
              required
              type="email"
              name="email"
              label="이메일"
              placeholder="이메일을 입력해주세요"
              inputProps={{
                autoComplete: 'email',
              }}
              id="email"
              fullWidth
            />
            <TextFieldHook
              control={control}
              required
              label="비밀번호"
              name="password"
              placeholder="비밀번호를 입력해주세요"
              fullWidth
              type={showPassword ? 'text' : 'password'}
              inputProps={{
                autoComplete: 'current-password',
              }}
              id="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      aria-label="toggle password visibility"
                      onClick={toggleShowPassword}
                      edge="end"
                    >
                      {showPassword ? <Eye /> : <EyeOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              size="xLarge"
              disabled={isSubmitting}
              type="submit"
              variant="contained"
              color={isNewWebsite ? 'secondary' : 'primary'}
              fullWidth
            >
              로그인
            </Button>
          </div>
          <div className={otherActionsStyle}>
            <Button
              data-ga-name="findEmail_btn"
              onClick={handleClickFindEmailOpen}
              css={customButtonStyle}
              variant="text"
              color="primary"
            >
              계정 이메일 찾기
            </Button>
            <Divider className={dividerStyle} orientation="vertical" flexItem />
            <Button
              data-ga-name="resetPw_btn"
              onClick={openResetPasswordOpen}
              css={customButtonStyle}
              variant="text"
              color="primary"
            >
              비밀번호 재설정
            </Button>
            <Divider className={dividerStyle} orientation="vertical" flexItem />
            <Link href="/signup">
              <Button
                fullWidth
                data-ga-on="click"
                data-ga-name="signup_btn"
                disabled={isSubmitting}
                variant="text"
                color="primary"
              >
                회원가입
              </Button>
            </Link>
          </div>
        </form>
      </Paper>
      <FindEmailDialog
        key={`FindEmailDialog-${openFindEmail}`}
        open={openFindEmail}
        onClose={handleCloseFindEmail}
      />
      <ResetPasswordDialog
        key={`ResetPasswordDialog-${openResetPassword}`}
        open={openResetPassword}
        onClose={hideResetPassword}
      />
    </div>
  );
};

export default LoginForm;
